import { Helmet } from 'react-helmet-async';
import ContactForm from '../components/ContactForm';
import { companyInfo, openingHours } from '../data/siteData';

export default function ContactPage() {
  return (
    <>
      <Helmet>
        <title>Kontakt - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Kontakt Brønshøj Ure & Guld på Frederikssundsvej 306A, 2700 Brønshøj. Tlf: 38 28 10 74. Besøg butikken eller send en besked."
        />
        <meta property="og:title" content="Kontakt - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Kontakt Brønshøj Ure & Guld på Frederikssundsvej 306A, 2700 Brønshøj. Tlf: 38 28 10 74. Besøg butikken eller send en besked."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/elegant-black-and-white-logo-design-2.png"
        />
      </Helmet>

      {/* Header */}
      <section className="bg-[#0B132B] text-white pt-36 sm:pt-40 lg:pt-56 pb-16 sm:pb-20 lg:pb-28 border-b border-white/10 text-center">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] font-semibold block">
            BESØG OS I BRØNSHØJ
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white">
            KONTAKT BRØNSHØJ URE & GULD
          </h1>
          <p className="text-xs sm:text-sm text-stone-300 max-w-2xl mx-auto uppercase tracking-widest font-sans leading-relaxed">
            Vi byder dig altid velkommen med smil, høj faglighed og personlig service i vor forretning.
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            
            {/* Contact Information & Hours */}
            <div className="lg:col-span-5 space-y-8">
              
              <div className="bg-[#F2EFE9] border border-[#E2DDD3] p-8 sm:p-10 space-y-6 text-center rounded-none shadow-none">
                <div className="w-[1px] h-8 bg-[#B5A897] mx-auto"></div>
                <h2 className="font-serif text-2xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                  Firmainfo
                </h2>

                <div className="space-y-4 text-xs uppercase tracking-wider text-stone-700">
                  <div>
                    <p className="text-[10px] text-[#B5A897]">NAVN</p>
                    <p className="font-bold text-[#1A1A1A]">{companyInfo.name}</p>
                    <p className="text-[#1A1A1A]">{companyInfo.address}, {companyInfo.zipCity}</p>
                  </div>

                  <div>
                    <p className="text-[10px] text-[#B5A897]">TELEFON</p>
                    <a
                      href={`tel:${companyInfo.phoneRaw}`}
                      className="font-bold text-[#1A1A1A] hover:text-[#B5A897] text-sm"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>

                  <div>
                    <p className="text-[10px] text-[#B5A897]">E-MAIL</p>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="font-bold text-[#1A1A1A] hover:text-[#B5A897] text-sm"
                    >
                      {companyInfo.email}
                    </a>
                  </div>

                  <div>
                    <p className="text-[10px] text-[#B5A897]">CVR-NUMMER</p>
                    <p className="font-mono text-stone-800">{companyInfo.cvr}</p>
                  </div>
                </div>

                {/* Map Button */}
                <div className="pt-2">
                  <a
                    href="https://maps.google.com/maps?q=Frederikssundsvej+306A+2700+Brønshøj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block border border-[#0B132B] text-[#0B132B] bg-transparent hover:bg-[#0B132B] hover:text-white transition-all text-xs tracking-[0.2em] uppercase px-6 py-3 rounded-none font-medium w-full"
                  >
                    ÅBN I GOOGLE MAPS
                  </a>
                </div>
              </div>

              {/* Opening Hours Box */}
              <div className="bg-[#F2EFE9] border border-[#E2DDD3] p-8 sm:p-10 space-y-4 text-center rounded-none shadow-none">
                <div className="w-[1px] h-6 bg-[#B5A897] mx-auto"></div>
                <h3 className="font-serif text-xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                  ÅBNINGSTIDER
                </h3>
                <div className="space-y-1 text-xs uppercase tracking-wider">
                  {openingHours.map((item) => (
                    <div key={item.day} className="flex justify-between py-2 border-b border-[#E2DDD3]">
                      <span className="text-stone-500 font-medium">{item.day}:</span>
                      <span className={`font-semibold ${item.hours === 'LUKKET' ? 'text-stone-400' : 'text-[#1A1A1A]'}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm variant="form" />
            </div>

          </div>

          {/* Map Preview Embed */}
          <div className="bg-[#F2EFE9] border border-[#E2DDD3] p-6 text-center space-y-4 rounded-none shadow-none">
            <h3 className="font-serif text-xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
              FIND OS I BRØNSHØJ
            </h3>
            <div className="w-full h-80 rounded-none overflow-hidden border border-[#E2DDD3]">
              <iframe
                title="Brønshøj Ure & Guld Placering"
                src="https://maps.google.com/maps?q=Frederikssundsvej%20306A%202700%20Brønshøj&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
